import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { useTelegramWebApp } from '../../../hooks/useTelegramWebApp';
import styles from './Modal.module.css';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  /** Modal title (rendered in header). When omitted, header is rendered only if showClose is true. */
  title?: string;
  /** Center the title. Defaults to true when there is no close button, false otherwise. */
  centerTitle?: boolean;
  /** Show the ✕ close button in the header. Default: true. */
  showClose?: boolean;
  /** Width preset. sm=380, md=440, lg=540, xl=820. Default: md. */
  size?: ModalSize;
  /** Footer content (e.g. action buttons). */
  footer?: ReactNode;
  /** Body content. */
  children: ReactNode;
  /** Close handler — called on backdrop click and ✕ button. */
  onClose: () => void;
  /** Optional className for the modal panel. */
  className?: string;
  /** Optional className for the body wrapper. */
  bodyClassName?: string;
  /** Keep the modal centered on small screens instead of the default bottom-sheet layout.
   *  Use for compact confirmation dialogs where a sheet feels heavy. */
  centerOnMobile?: boolean;
}

export const Modal = ({
  title,
  centerTitle,
  showClose = true,
  size = 'md',
  footer,
  children,
  onClose,
  className,
  bodyClassName,
  centerOnMobile = false,
}: ModalProps) => {
  useLockBodyScroll(true);
  const { haptic } = useTelegramWebApp();

  const handleBackdropClick = (e: React.MouseEvent | React.TouchEvent) => {
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      haptic.impact('light');
      onClose();
    }
  };

  const handlePanelClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  };

  const handleCloseClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic.impact('light');
    onClose();
  };

  const titleCentered = centerTitle ?? !showClose;
  const hasHeader = Boolean(title) || showClose;

  return createPortal(
    <div
      className={`${styles.backdrop} ${centerOnMobile ? styles.backdropCentered : ''}`}
      onClick={handleBackdropClick}
      onTouchEnd={handleBackdropClick}
    >
      <div
        className={`${styles.modal} ${styles[`size_${size}`]} ${centerOnMobile ? styles.modalCentered : ''} ${className || ''}`}
        onClick={handlePanelClick}
        onTouchEnd={handlePanelClick}
      >
        {hasHeader && (
          <div className={styles.header}>
            {title && (
              <h3 className={`${styles.title} ${titleCentered ? styles.titleCentered : ''}`}>
                {title}
              </h3>
            )}
            {showClose && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleCloseClick}
                onTouchEnd={handleCloseClick}
                aria-label="Close"
              >
                ✕
              </button>
            )}
          </div>
        )}

        <div className={`${styles.body} ${bodyClassName || ''}`}>{children}</div>

        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
